import React from "react";

import AuthIcon from "../AuthIcon";
import { Link, useNavigate } from "react-router-dom";

import * as Yup from "yup";
import { useFormik as useFormic } from "formik";
import { doFirstTimeLogin } from "../../../services/auth.service";
import { doLoginRoute } from "../../../slices/thunk";
import { toast, ToastContainer } from "react-toastify";
import { doExpireShortUrl } from "../../../services/project.service";

const BasicCreatePassword = ({ userId, shortUrlCode }: { userId: string, shortUrlCode?: string }) => {

  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  document.title = "Create Password | BatamX - Admin & Dashboard";

  const validation: any = useFormic({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      userId: userId,
      password: '',
      passwordConfirm: '',
    },
    validationSchema: Yup.object({
      userId: Yup.string().required("User Id is required"),
      password: Yup.string().required("Please Enter Your Password"),
      passwordConfirm: Yup.string().oneOf([Yup.ref('password')], 'Confirm Password must be same')
    }),
    onSubmit: (values: any) => {
      setLoading(true)
      doFirstTimeLogin(values.userId, values.password).then((response: any) => {
        toast.success("Password Reset Successfully", { autoClose: 2000 })
        validation.resetForm()

        localStorage.setItem("accessToken", response.accessToken)
        if (shortUrlCode) {
          doExpireShortUrl(shortUrlCode)
        }

        const user = response.user
        if (!['admin', 'batamx', 'owner'].includes(user.role)) {
          localStorage.removeItem("accessToken")
          setTimeout(() => {
            navigate("/login")
          }, 5000)
          return
        }

        localStorage.setItem("authUser", JSON.stringify(user))
        doLoginRoute(navigate)
      }).catch((error: any) => {
        setLoading(false)
        toast.error("Password Reset Failed", { autoClose: 2000 })
      })
    }
  });

  React.useEffect(() => {
    const bodyElement = document.body;

    bodyElement.classList.add('flex', 'items-center', 'justify-center', 'min-h-screen', 'py-16', 'lg:py-10', 'bg-slate-50', 'dark:bg-zink-800', 'dark:text-zink-100', 'font-public');

    return () => {
      bodyElement.classList.remove('flex', 'items-center', 'justify-center', 'min-h-screen', 'py-16', 'lg:py-10', 'bg-slate-50', 'dark:bg-zink-800', 'dark:text-zink-100', 'font-public');
    }
  }, []);

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />
      <div className="relative">
        <AuthIcon />

        <div className="mb-0 w-screen lg:w-[500px] card shadow-lg border-none shadow-slate-100 dark:shadow-zink-500/20 relative">
          <div className="!px-10 !py-12 card-body">

            <div className="mt-8 text-center">
              <h4 className="mb-2 text-custom-500 dark:text-custom-500">Set a New Password</h4>
              <p className="mb-8 text-slate-500 dark:text-zink-200">Your new password should be distinct from any of your prior passwords</p>
            </div>

            <form autoComplete="off" id="signInForm"
              onSubmit={(event: any) => {
                event.preventDefault();
                validation.handleSubmit();
                return false;
              }}>
              <div className="mb-3">
                <label htmlFor="passwordInput" className="inline-block mb-2 text-base font-medium">Password</label>
                <input type="password" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" required placeholder="Password" id="passwordInput"
                  name="password"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.password || ""} />
                {validation.touched.password && validation.errors.password ? (
                  <div id="password-error" className="mt-1 text-sm text-red-500">{validation.errors.password}</div>
                ) : null}
              </div>
              <div className="mb-3">
                <label htmlFor="passwordConfirmInput" className="inline-block mb-2 text-base font-medium">Confirm Password</label>
                <input type="password" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" required placeholder="Confirm password" id="passwordConfirmInput"
                  name="passwordConfirm"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.passwordConfirm || ""} />
                {validation.touched.passwordConfirm && validation.errors.passwordConfirm ? (
                  <div id="passwordConfirm-error" className="mt-1 text-sm text-red-500">{validation.errors.passwordConfirm}</div>
                ) : null}
              </div>
              <div className="mt-8">
                <button disabled={loading} type="submit" className="w-full text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">
                  Reset Password
                </button>
              </div>
              <div className="mt-4 text-center">
                <p className="mb-0">Hold on, I've got my password... <Link to="/login" className="underline fw-medium text-custom-500"> Click here </Link> </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default BasicCreatePassword;